import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationService } from '../../../common/services/navigation.service';
import { PurchasingRfqRequestService } from '../../../common/services/purchasing-rfq-request.service';
import { Router } from '@angular/router';
import { FilterParam, GroupHeader, SearchData, SearchListNewComponent } from '../../../common/components/search-list-new/search-list-new.component';
import { PurchaseOrder } from '../../resources/purchaseOrder';
import { UtilityService } from '../../../common/services/utility.service';
import { PurchasingService } from '../../services/purchasing.service';
import { ProcurementQueueTicket, ProcurementQueueTicketType } from '../../resources/procurement-ticket';
import { ItemType, ItemTypeChipComponent } from '@cots/inventory/components/item-type-chip/item-type-chip.component';
import { getValidityClass, getValidityName, PurchasingSheetValidityLevel } from '@cots/purchasing/resources/purchasingSheet';
import { MatDialog } from '@angular/material/dialog';
import { ProcurementQuickPoComponent } from '../procurement-quick-po/procurement-quick-po.component';

@Component({
  selector: 'procurement-queue',
  templateUrl: './procurement-queue.component.html',
  styleUrls: ['./procurement-queue.component.less']
})
export class ProcurementQueueComponent {

  @ViewChild(SearchListNewComponent) searchListNew: SearchListNewComponent<ProcurementQueueTicket>; 

  constructor(navService: NavigationService, private service: PurchasingService, private router: Router, private dialog: MatDialog) {
    navService.setPageTitle("Procurement");
  }

  public search({ searchText, page, sort, groupBy, filters }: SearchData) {
    return this.service.searchProcurementTickets(searchText, page.pageIndex, 12, sort.active, sort.direction, groupBy, filters);
  }
  
  public getValidityClass = getValidityClass;
  public getValidityName = getValidityName;


  public async doQuickPo(item: ProcurementQueueTicket) {
    if (!item.workOrder && !item.purchasingSheetItem?.purchasingSheet?.requisition) return;
    const ref = this.dialog.open(ProcurementQuickPoComponent, {
      minWidth: '40vw',
      data: item
    });
    const data = await ref.afterClosed().toPromise();
    if (!data) return;
    const { vendor, qty, useExistingQuote } = data;
    this.searchListNew.loading = true;
    const id = await this.service.quickPoFromSheetItem(item.procurementQueueTicketId, useExistingQuote, vendor, qty, item.materialId).toPromise();
    this.searchListNew.loading = false;
    this.searchListNew.forceUpdate();
    window.open('/purchasing/order/' + id, '_blank').focus();
  }

  onSelect(record: ProcurementQueueTicket): void {
    if (this.searchListNew.loading) return;
    if (record.ticketType == ProcurementQueueTicketType.QUOTING) {
      this.router.navigate(['/purchasing/procurement/', record.procurementQueueTicketId]);
    } else if (record.ticketType == ProcurementQueueTicketType.ACQUISITION) {
      this.doQuickPo(record);
    } 
  }

  public displayedColumns = [
    "ticketType",
    "item",
    "quantity",
    "sourceTicket",
    "dueDate",
    "validity"
  ]

  public getTicketTypeClass(type: number) {
    switch (type) {
      case 0:
        return "ticket--acquisition";
      case 1:
        return "ticket--quoting";
      default:
        return "";
    }
  }

  public getTicketTypeText(type: number) {
    switch (type) {
      case 0:
        return "Acquisition";
      case 1:
        return "Quoting";
      default:
        return "";
    }
  }

  public readonly availableTypes = [0, 1] as const;
  public readonly itemTypes = ["purchased", "material", "process"] as const;

  public readonly filterParams: FilterParam[] = [{
    categoryName: 'ticketType',
    categoryTitle: 'Type',
    options: this.availableTypes.map(s => ({
      class: this.getTicketTypeClass(s),
      title: this.getTicketTypeText(s),
      value: s
    }))
  }, {
    categoryName: 'itemType',
    categoryTitle: 'Item',
    options: this.itemTypes.map(s => ({
      class: `item-type--${s.toUpperCase()} text-white`,
      title: ItemTypeChipComponent.getItemTypeName(s.toUpperCase() as ItemType),
      value: s,
      icon: ItemTypeChipComponent.getItemTypeIcon(s.toUpperCase() as ItemType),
    }))
  }, {
    categoryName: 'validity',
    categoryTitle: 'Validity',
    options: Object.values(PurchasingSheetValidityLevel).filter(v => typeof v === 'number').map((s: number) => ({
      class: this.getValidityClass(s),
      title: this.getValidityName(s),
      value: s,
    }))
  }];

  public readonly defaultFilters = { ticketType: this.availableTypes, itemType: this.itemTypes }

  public groupHeaderClassPredicate(item: GroupHeader, groupBy: string) {
    if (groupBy === 'dueDate') {
      switch (item.groupKey) {
        case "overdue":
          return "alert-danger text-danger border-bottom border-danger"
        default:
          return "bg-light border-bottom";
      }
    }
    if (groupBy === 'ticket') {
        return "bg-light border-bottom";
    }
  }

  public getItemName(item: ProcurementQueueTicket) {
    if (item.material) return item.material.materialName;
    else if (item.purchasedItem) return item.purchasedItem.description;
    // TODO name other steps
    else if (item.outsideProcessDescription) return `${item.outsideProcessDescription.steps[0]?.station.name} for ${item.outsideProcessDescription.product.partNumber} Rev. ${item.outsideProcessDescription.product.revision}`;
    else if (item.station) return `${item.station.name}`;
    else if (item.paint) return `${item.paint}`;
    else return "Unknown Item";
  }

  public getSourceTicketName(item: ProcurementQueueTicket) {
    if (item.workOrder) return `WO #${item.workOrder.workOrderNumber}`;
    else if (item.estimate) return item.estimate.quoteNumber ?? item.estimate.orderNumber;
    else if (item.purchaseOrder) return item.purchaseOrder.purchaseOrderNumber;
    else if (item.purchasingSheetItem?.purchasingSheet?.requisition) return item.purchasingSheetItem?.purchasingSheet?.requisition?.requisitionNumber;
    else return ""
  }

  public convertItemForChip(item: ProcurementQueueTicket) {
    if (item.outsideProcessDescription) item.station = item.outsideProcessDescription.steps?.[0]?.station;
    return item;
  }

  public getTotalAssignmentQty(item: ProcurementQueueTicket) {
    if (!item.purchasingSheetItem) return 0;
    return item.purchasingSheetItem.assignments.reduce((acc, a) => acc + a.qtyAssigned, 0);
  }



}

